import React, {useEffect, useRef, useState} from 'react'
import Button from 'lib/ui/Button'
import styled from 'styled-components'
import TextField from '@material-ui/core/TextField'
import {onChangeStringHandler} from 'lib/dom'
import Icon from 'lib/ui/Icon'
import {useDebouncedHandler} from 'lib/debounce'
import {useUserOrganizations} from 'obvio/Organizations/UserOrganizationsProvider'

export default function Search() {
  const {searchQuery, setSearchQuery} = useUserOrganizations()
  const [value, setValue] = useDebouncedHandler(
    searchQuery,
    setSearchQuery,
    400,
  )
  const [open, setOpen] = useState(searchQuery !== '')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) {
      return
    }

    inputRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) {
      return
    }

    function mouseClickHandler(event: MouseEvent) {
      const target = event.target as HTMLElement | null
      if (target?.closest(`[data-header-search]`)) {
        return
      }

      if (!value) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', mouseClickHandler)
    return () => document.removeEventListener('mousedown', mouseClickHandler)
  }, [open, value])

  const handleIconClick = () => {
    if (!open) {
      setOpen(true)
      return
    }

    if (open && value === '') {
      setOpen(false)
      return
    }
  }

  return (
    <SearchRoot data-header-search>
      <ExpandTrack open={open}>
        <SearchField
          inputRef={inputRef}
          variant="outlined"
          size="small"
          margin="none"
          placeholder="Search"
          value={value}
          onChange={onChangeStringHandler(setValue)}
          inputProps={{
            'aria-label': 'search for organization',
          }}
        />
      </ExpandTrack>
      <Button type="button" aria-label="Open search" onClick={handleIconClick}>
        <SearchIcon className="fa-solid fa-magnifying-glass" iconSize={20} />
      </Button>
    </SearchRoot>
  )
}

const SearchRoot = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[2]};
  margin-right: ${(props) => props.theme.spacing[2]};
`

const ExpandTrack = styled.div<{open: boolean}>`
  flex-shrink: 0;
  width: ${(props) => (props.open ? `220px` : '0')};
  overflow: hidden;
  transition: width 200ms ease-out;
  display: flex;
  align-items: center;
`

const SearchField = styled(TextField)`
  flex-shrink: 0;
  width: 220px;
  margin: 0;

  .MuiOutlinedInput-root {
    box-sizing: border-box;
  }
`

const SearchIcon = styled(Icon)`
  color: ${(props) => props.theme.colors.gray200};
`
